import { Visibility, VisibilityOff } from "@mui/icons-material";
import { Box, IconButton, InputAdornment, Link, Typography } from "@mui/material";
import type { FC } from "react";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { Link as RouterLink } from "react-router-dom";
import { auth } from "../auth/auth";
import AsyncLoadingButton from "../components/asyncLoadingButton/AsyncLoadingButton";
import FormTextField, {
  emailValidationPattern,
  passwordValidationPattern,
} from "../components/formComponents/FormTextField";

interface EmailPasswordLoginFormState {
  readonly email: string;
  readonly password: string;
}

interface Props {
  readonly onLoginSuccess: () => void | Promise<void>;
  readonly onLoginFailed: (error: unknown) => void;
}

const EmailPasswordLogin: FC<Props> = ({ onLoginSuccess, onLoginFailed }) => {
  const [showPassword, setShowPassword] = useState(false);
  const { control, handleSubmit } = useForm<EmailPasswordLoginFormState>({
    defaultValues: { email: "", password: "" },
  });

  const handleClickShowPassword = () => {
    setShowPassword((prev) => !prev);
  };

  const onSubmit = async (email: string, password: string) => {
    try {
      await auth.signInWithEmailAndPassword(email, password);
      await onLoginSuccess();
    } catch (error) {
      onLoginFailed(error);
    }
  };

  return (
    <>
      <Typography
        variant="h4"
        gutterBottom
        color="text.primary"
        sx={{
          fontWeight: "bold",
        }}
      >
        Witaj ponownie!
      </Typography>

      <Typography variant="body1" color="textSecondary" sx={{ mb: 2 }}>
        Zaloguj się do platformy PrzezMorze B2B
      </Typography>
      <FormTextField
        margin="normal"
        required
        fullWidth
        id="email"
        label="Adres e-mail"
        name="email"
        autoComplete="email"
        autoFocus
        type="email"
        variant="outlined"
        control={control}
        rules={{ pattern: emailValidationPattern }}
        InputProps={{ sx: { borderRadius: 2 } }}
      />
      <FormTextField
        margin="normal"
        required
        fullWidth
        id="password"
        name="password"
        label="Hasło"
        type={showPassword ? "text" : "password"}
        autoComplete="current-password"
        variant="outlined"
        control={control}
        rules={{ pattern: passwordValidationPattern }}
        InputProps={{
          sx: { borderRadius: 2 },
          endAdornment: (
            <InputAdornment position="end">
              <IconButton aria-label="toggle password visibility" onClick={handleClickShowPassword} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 1, mb: 3 }}>
        <Link
          component={RouterLink}
          to={`/sign-up`}
          variant="body2"
          sx={{ fontWeight: 500, textDecoration: "none", color: "primary.main" }}
        >
          Zarejestruj się
        </Link>
        <Link
          component={RouterLink}
          to={`/reset-password`}
          variant="body2"
          sx={{ fontWeight: 500, textDecoration: "none", color: "primary.main" }}
        >
          Zapomniałeś hasła?
        </Link>
      </Box>
      <AsyncLoadingButton
        onClick={handleSubmit((s) => onSubmit(s.email, s.password))}
        type="submit"
        fullWidth
        variant="contained"
        color="primary"
        size="large"
        sx={{ py: 1.5, mb: 2, fontSize: "1.1rem", borderRadius: 16, boxShadow: "0 4px 12px rgba(53, 87, 161, 0.3)" }}
      >
        Zaloguj się{" "}
      </AsyncLoadingButton>
    </>
  );
};

export default EmailPasswordLogin;
